// Modal reutilizable "Corregir costo" (revalorización): fija el costo promedio
// de las unidades YA existentes de un producto en una bodega, sin cambiar la
// cantidad. RPC inventario.inv_recostear (script 649). Ver Valorizacion.tsx.
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import {
  BuscadorProducto, Campo, Location, Modal, ProductoOpt, fieldCls,
  invRpc, locationsList, money, num, productosOptList, toNum,
} from './shared';

interface StockActualRow { unidades: number; costo_promedio: number; valor: number }

interface RecostearResultado {
  ok?: boolean; sin_cambios?: boolean;
  costo_anterior?: number; costo_nuevo?: number; costo?: number;
  unidades: number; delta_valor?: number;
}

export function ModalCorregirCosto({
  locationIdInicial, productoIdInicial, onClose, onOk,
}: {
  locationIdInicial?: string; productoIdInicial?: string;
  onClose: () => void; onOk: () => void;
}) {
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });

  const [locationId, setLocationId] = useState(locationIdInicial ?? '');
  const [productoId, setProductoId] = useState(productoIdInicial ?? '');
  const [nuevoCosto, setNuevoCosto] = useState('');
  const [motivo, setMotivo] = useState('');

  const { data: stock, isFetching: cargandoStock } = useQuery<StockActualRow | null>({
    queryKey: ['inv-stock-actual-1', locationId, productoId],
    queryFn: async () => {
      if (!locationId || !productoId) return null;
      const rows = (await apiClient.get<StockActualRow[]>(
        `/rest/inventario.stockcurrent?location_id=eq.${locationId}&producto_id=eq.${productoId}&select=unidades,costo_promedio,valor`
      )).data;
      return rows[0] ?? null;
    },
    enabled: !!locationId && !!productoId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const unidades = Number(stock?.unidades ?? 0);
  const costoActual = Number(stock?.costo_promedio ?? 0);
  const valorActual = Number(stock?.valor ?? 0);
  const hayStock = !!stock && unidades > 0;

  const nuevo = toNum(nuevoCosto);
  const nuevoValido = nuevoCosto !== '' && nuevo >= 0;
  const nuevoValor = hayStock ? unidades * nuevo : 0;
  const delta = hayStock ? unidades * (nuevo - costoActual) : 0;

  const mut = useMutation({
    mutationFn: () => invRpc<RecostearResultado>('inv_recostear', {
      _location: locationId, _producto: productoId, _nuevo_costo: nuevo,
      _motivo: motivo.trim() || null,
    }),
    onSuccess: (r) => {
      if (r.sin_cambios) {
        toast.info('El costo ingresado es igual al actual: sin cambios.');
        onOk();
        return;
      }
      const d = Number(r.delta_valor ?? 0);
      const signo = d >= 0 ? '+' : '−';
      toast.success(
        `Costo actualizado: ${num(r.unidades, 2)} u a ${money(r.costo_nuevo ?? nuevo)} (Δ ${signo}${money(Math.abs(d))})`
      );
      onOk();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = !!locationId && !!productoId && hayStock && nuevoValido && !mut.isPending;

  return (
    <Modal title="Corregir costo (revalorización)" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Fija el costo promedio de las unidades ya existentes de un producto en una bodega,
          sin cambiar la cantidad. La diferencia se contabiliza contra el concepto
          "revalorización".
        </p>

        <Campo label="Bodega">
          <select className={fieldCls} value={locationId}
            onChange={(e) => { setLocationId(e.target.value); }} disabled={mut.isPending}>
            <option value="">— Selecciona —</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>

        <Campo label="Producto">
          <BuscadorProducto value={productoId} onChange={setProductoId} productos={productos}
            disabled={mut.isPending || !locationId} allowClear
            placeholder={locationId ? 'Buscar producto…' : 'Primero elige la bodega'} />
        </Campo>

        {locationId && productoId && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
            {cargandoStock ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Consultando stock…</div>
            ) : !hayStock ? (
              <div className="text-red-600 dark:text-red-400">Sin stock de este producto en la bodega seleccionada.</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div><div className="text-xs text-muted-foreground">Unidades</div><div className="font-semibold">{num(unidades, 2)}</div></div>
                <div><div className="text-xs text-muted-foreground">Costo actual</div><div className="font-semibold">{money(costoActual)}</div></div>
                <div><div className="text-xs text-muted-foreground">Valor actual</div><div className="font-semibold">{money(valorActual)}</div></div>
              </div>
            )}
          </div>
        )}

        <Campo label="Nuevo costo unitario">
          <input type="number" min="0" step="0.0001" className={fieldCls} value={nuevoCosto}
            onChange={(e) => setNuevoCosto(e.target.value)} disabled={!hayStock || mut.isPending}
            placeholder="0.00" />
        </Campo>

        {hayStock && nuevoValido && (
          <div className="rounded-md border p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Nuevo valor</span><span className="font-semibold">{money(nuevoValor)}</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Diferencia (Δ)</span>
              <span className={`font-semibold ${delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : delta < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                {delta >= 0 ? '+' : '−'}{money(Math.abs(delta))}
              </span>
            </div>
          </div>
        )}

        <Campo label="Motivo (opcional)">
          <textarea className={fieldCls} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            disabled={mut.isPending} placeholder="Ej.: costo cargado en $0 durante la carga inicial" />
        </Campo>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!puedeGuardar} isLoading={mut.isPending}>
            Corregir costo
          </Button>
        </div>
      </div>
    </Modal>
  );
}
